import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import Layout from '../components/Layout';
import { AiDraftResult, AiRewriteResult, assessmentsService } from '../api/assessmentsService';
import { InterviewGuideTab, InterviewNotesPanel, useInterviewGuide } from '../components/InterviewGuide';
import { ScorecardSummary, ScorecardTab, buildReportDraftFromScorecard, useScorecard } from '../components/Scorecard';
import { AiDraftModal, AiRewriteModal } from '../components/AssessmentReportAi';
import { saveStateLabel, useDebouncedAutosave } from '../hooks/useDebouncedAutosave';
import { useAuth } from '../contexts/AuthContext';

const displayCandidateName = (c: any) =>
  c?.name || [c?.firstName, c?.lastName].filter(Boolean).join(' ') || '—';

const displaySpouseName = (c: any) =>
  c?.spouseName || [c?.wifeFirstName, c?.wifeLastName].filter(Boolean).join(' ') || '';

type RatingValue = 'red' | 'yellow' | 'green';

/** Which field an "Insert" action should write into. */
type InsertTarget = 'content' | 'afterPartNotes';

const ratingOptions: Array<{ value: RatingValue; label: string; className: string }> = [
  { value: 'red', label: 'Red', className: 'text-red-700' },
  { value: 'yellow', label: 'Yellow', className: 'text-yellow-700' },
  { value: 'green', label: 'Green', className: 'text-green-700' },
];

const statusPillClass: Record<string, string> = {
  draft: 'bg-gray-100 text-gray-700',
  submitted: 'bg-green-100 text-green-800',
  returned: 'bg-amber-100 text-amber-800',
  reviewed: 'bg-blue-100 text-blue-800',
  approved: 'bg-green-100 text-green-800',
  unassigned: 'bg-gray-100 text-gray-500',
};

const statusLabel: Record<string, string> = {
  draft: 'Draft',
  submitted: 'Submitted',
  returned: 'Returned for edits',
  reviewed: 'Reviewed',
  approved: 'Approved',
};

const appendText = (existing: string, addition: string) => (
  existing.trim() ? `${existing.trimEnd()}\n\n${addition}` : addition
);

const RatingSelector: React.FC<{
  label: string;
  name: string;
  value: RatingValue | '';
  onChange: (value: RatingValue | '') => void;
  disabled?: boolean;
}> = ({ label, name, value, onChange, disabled }) => (
  <div className="space-y-2">
    <div className="text-sm font-medium text-gray-700">{label}</div>
    <div className="flex flex-wrap gap-3">
      {ratingOptions.map((option) => (
        <label
          key={option.value}
          className={[
            'flex items-center gap-2 rounded-md border px-3 py-2 text-sm',
            value === option.value ? 'border-primary-500 bg-primary-50' : 'border-gray-200',
            disabled ? 'opacity-70 cursor-not-allowed' : 'cursor-pointer',
          ].join(' ')}
        >
          <input
            type="radio"
            name={name}
            value={option.value}
            checked={value === option.value}
            onChange={() => onChange(option.value)}
            disabled={disabled}
          />
          <span className={option.className}>{option.label}</span>
        </label>
      ))}
      <button
        type="button"
        className="text-xs text-gray-500 underline underline-offset-2"
        onClick={() => onChange('')}
        disabled={disabled}
      >
        Clear
      </button>
    </div>
  </div>
);

const AssessmentReport: React.FC = () => {
  const { id, assignmentId } = useParams();
  const assessmentId = useMemo(() => parseInt(id || '0', 10), [id]);
  const reportAssignmentId = useMemo(() => parseInt(assignmentId || '0', 10), [assignmentId]);
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const { data, isLoading, isError } = useQuery({
    queryKey: ['assessments', assessmentId, 'report-assignments', reportAssignmentId],
    queryFn: () => assessmentsService.getReportAssignment(assessmentId, reportAssignmentId),
    enabled: Number.isFinite(assessmentId) && assessmentId > 0 && Number.isFinite(reportAssignmentId) && reportAssignmentId > 0,
  });

  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [initialOverallRating, setInitialOverallRating] = useState<RatingValue | ''>('');
  const [initialAreaRating, setInitialAreaRating] = useState<RatingValue | ''>('');
  const [afterPartNotes, setAfterPartNotes] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'guide' | 'scorecard' | 'report' | null>(null);
  const [insertTarget, setInsertTarget] = useState<InsertTarget>('content');
  const [showSubmitCheck, setShowSubmitCheck] = useState(false);

  const [draftModalOpen, setDraftModalOpen] = useState(false);
  const [draftResult, setDraftResult] = useState<AiDraftResult | null>(null);
  const [draftError, setDraftError] = useState<string | null>(null);
  const [rewriteModalOpen, setRewriteModalOpen] = useState(false);
  const [rewriteResult, setRewriteResult] = useState<AiRewriteResult | null>(null);
  const [rewriteError, setRewriteError] = useState<string | null>(null);
  const [rewriteSource, setRewriteSource] = useState('');
  const [aiAssist, setAiAssist] = useState<Record<string, string> | null>(null);

  const assignment = data?.data;
  const latestReport = assignment?.latestReport || null;
  const isPrimary = assignment?.assessorId === user?.id;
  const isAssistant = assignment?.assistantId === user?.id;
  const canEdit = isPrimary || isAssistant;
  const canEditReport = canEdit && (!latestReport || ['draft', 'returned', 'submitted'].includes(latestReport.status));
  const reportStatus = latestReport?.status || 'draft';
  const isLocked = Boolean(latestReport && !['draft', 'returned', 'submitted'].includes(latestReport.status));
  const isSubmitted = reportStatus === 'submitted';

  const hydratedForRef = useRef<number | null>(null);
  const editedRef = useRef(false);

  // Hydrate once per assignment. Refetches triggered by autosave must not
  // overwrite characters typed while the request was in flight.
  useEffect(() => {
    if (!assignment || hydratedForRef.current === reportAssignmentId) return;
    const roleLabel = assignment.role?.displayName || assignment.role?.name || 'Assessment';
    setTitle(latestReport?.title || `${roleLabel} Report`);
    setContent(latestReport?.content || '');
    const reportAny = latestReport as any;
    setInitialOverallRating(reportAny?.initialOverallRating || reportAny?.metadata?.initialOverallRating || '');
    setInitialAreaRating(reportAny?.initialAreaRating || reportAny?.metadata?.initialAreaRating || '');
    setAfterPartNotes(reportAny?.afterPartNotes || reportAny?.metadata?.afterPartNotes || '');
    hydratedForRef.current = reportAssignmentId;
    editedRef.current = false;
  }, [assignment, latestReport, reportAssignmentId]);

  // Land on the interview guide until there is a report in progress.
  useEffect(() => {
    if (!assignment || activeTab) return;
    setActiveTab(latestReport ? 'report' : 'guide');
  }, [assignment, latestReport, activeTab]);

  const saveMutation = useMutation({
    mutationFn: (payload: Parameters<typeof assessmentsService.saveReport>[2]) =>
      assessmentsService.saveReport(assessmentId, reportAssignmentId, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['assessments', assessmentId, 'report-assignments'] });
      queryClient.invalidateQueries({ queryKey: ['assessments', assessmentId, 'report-assignments', reportAssignmentId] });
      setErrorMessage(null);
    },
    onError: (err: any) => {
      setErrorMessage(err?.response?.data?.message || 'Failed to save report.');
    },
  });

  const buildPayload = useCallback((nextStatus: 'draft' | 'submitted') => ({
    title: title.trim(),
    content: content.trim(),
    status: nextStatus,
    initialOverallRating: initialOverallRating || null,
    initialAreaRating: initialAreaRating || null,
    afterPartNotes: afterPartNotes.trim() || null,
    ...(aiAssist ? { metadata: { aiAssist } } : {}),
  }), [title, content, initialOverallRating, initialAreaRating, afterPartNotes, aiAssist]);

  // A submitted report is only ever updated on an explicit click, so autosave
  // stays limited to drafts and reports that were returned for edits.
  const autosaveEnabled = Boolean(canEditReport && !isSubmitted && title.trim() && content.trim());

  const autosave = useDebouncedAutosave<ReturnType<typeof buildPayload>>({
    enabled: autosaveEnabled,
    save: (payload) => saveMutation.mutateAsync(payload),
  });

  const { schedule: scheduleAutosave, saveState, setSaveState, cancel: cancelAutosave } = autosave;

  // Queued edits target whichever assignment is current when they flush, so
  // drop them rather than writing one area's notes onto another.
  useEffect(() => cancelAutosave, [reportAssignmentId, cancelAutosave]);

  // Any field edit queues the whole form; the API is a full upsert either way.
  // Guarded by editedRef so simply opening the page never creates a report.
  const queueAutosave = useCallback(() => {
    if (!autosaveEnabled || !editedRef.current) return;
    scheduleAutosave(buildPayload('draft'));
  }, [autosaveEnabled, scheduleAutosave, buildPayload]);

  useEffect(() => {
    queueAutosave();
    // Field values are captured inside buildPayload, which queueAutosave depends on.
  }, [queueAutosave]);

  const touch = useCallback(() => { editedRef.current = true; }, []);

  const handleExplicitSave = (nextStatus: 'draft' | 'submitted') => {
    if (!title.trim() || !content.trim()) {
      setErrorMessage('Title and report notes are required.');
      return;
    }
    cancelAutosave();
    setSaveState('saving');
    saveMutation.mutate(buildPayload(nextStatus), {
      onSuccess: () => setSaveState('saved'),
      onError: () => setSaveState('error'),
    });
    setShowSubmitCheck(false);
  };

  const interviewGuideQuery = useInterviewGuide(assessmentId, reportAssignmentId);
  const guideData = interviewGuideQuery.data?.data;
  const aiEnabled = Boolean(guideData?.aiAssistEnabled) && canEditReport;
  const hasNotes = Boolean(
    guideData?.generalNotes?.trim()
    || (guideData?.sections || []).some((section) => section.questions.some((q) => q.notes?.trim())),
  );

  const scorecardQuery = useScorecard(assessmentId, reportAssignmentId);
  const scorecardData = scorecardQuery.data?.data;

  // Give a report that does not exist yet a starting point from the scorecard
  // narratives. Guarded so it never overwrites saved text or live typing, and
  // it deliberately leaves editedRef alone so a prefill alone never autosaves.
  const prefilledForRef = useRef<number | null>(null);
  useEffect(() => {
    if (!assignment || latestReport || !scorecardData) return;
    if (hydratedForRef.current !== reportAssignmentId) return;
    if (prefilledForRef.current === reportAssignmentId) return;
    if (editedRef.current || content.trim()) return;
    const draft = buildReportDraftFromScorecard(scorecardData.summary);
    if (!draft) return;
    setContent(draft);
    prefilledForRef.current = reportAssignmentId;
  }, [assignment, latestReport, scorecardData, reportAssignmentId, content]);

  const contentRef = useRef<HTMLTextAreaElement | null>(null);
  const afterPartRef = useRef<HTMLTextAreaElement | null>(null);

  const insertIntoTarget = (text: string) => {
    touch();
    if (insertTarget === 'afterPartNotes') {
      setAfterPartNotes((prev) => appendText(prev, text));
      afterPartRef.current?.focus();
    } else {
      setContent((prev) => appendText(prev, text));
      contentRef.current?.focus();
    }
  };

  const draftMutation = useMutation({
    mutationFn: () => assessmentsService.aiDraftReport(assessmentId, reportAssignmentId),
    onSuccess: (res) => {
      setDraftResult(res.data);
      setDraftError(null);
    },
    onError: (err: any) => {
      setDraftResult(null);
      setDraftError(err?.response?.data?.message || 'Could not draft from your notes. Try again in a moment.');
    },
  });

  const rewriteMutation = useMutation({
    mutationFn: (text: string) => assessmentsService.aiRewriteReportText(assessmentId, reportAssignmentId, text),
    onSuccess: (res) => {
      setRewriteResult(res.data);
      setRewriteError(null);
    },
    onError: (err: any) => {
      setRewriteResult(null);
      setRewriteError(err?.response?.data?.message || 'Could not rewrite this text. Try again in a moment.');
    },
  });

  const openDraftModal = () => {
    setDraftModalOpen(true);
    setDraftResult(null);
    setDraftError(null);
    draftMutation.mutate();
  };

  const openRewriteModal = () => {
    const source = content.trim();
    if (!source) {
      setErrorMessage('Write some report notes first, then ask for a candidate-ready rewrite.');
      return;
    }
    setRewriteSource(source);
    setRewriteModalOpen(true);
    setRewriteResult(null);
    setRewriteError(null);
    rewriteMutation.mutate(source);
  };

  const noteAiUse = (key: 'draftedAt' | 'rewrittenAt', model?: string) => {
    touch();
    setAiAssist((prev) => ({ ...(prev || {}), [key]: new Date().toISOString(), ...(model ? { model } : {}) }));
  };

  const candidate = assignment?.participant?.portalCandidate;
  const candidateName = displayCandidateName(candidate);
  const spouseName = displaySpouseName(candidate);
  const candidateLabel = spouseName ? `${candidateName} & ${spouseName}` : candidateName;
  const roleLabel = assignment?.role?.displayName || assignment?.role?.name || 'Assessment Role';

  const submitIssues = useMemo(() => {
    const issues: string[] = [];
    if (!title.trim()) issues.push('The report has no title.');
    if (!content.trim()) issues.push('Report notes are empty.');
    if (!initialOverallRating) issues.push('No overall rating is selected.');
    if (!initialAreaRating) issues.push('No rating for your area is selected.');
    return issues;
  }, [title, content, initialOverallRating, initialAreaRating]);

  return (
    <Layout>
      <div className="space-y-6 pb-24">
        <div className="bg-white shadow-sm rounded-xl p-4 sm:p-6">
          <div className="flex items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">{roleLabel} Report</h1>
              <p className="text-gray-600 mt-1">{candidateLabel}</p>
            </div>
            <Link to={`/assessments/${assessmentId}`} className="text-sm text-primary-600 hover:text-primary-700">
              Back to Assessment
            </Link>
          </div>
        </div>

        {isLoading && (
          <div className="bg-white shadow-sm rounded-xl p-4 sm:p-6 text-gray-700">Loading report…</div>
        )}

        {isError && (
          <div className="bg-white shadow-sm rounded-xl p-4 sm:p-6 text-red-700">Failed to load report.</div>
        )}

        {!isLoading && !isError && assignment && (
          <div className="bg-white shadow-lg rounded-xl p-4 sm:p-6 space-y-6">
            {errorMessage && (
              <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
                {errorMessage}
              </div>
            )}

            {!canEdit && !latestReport && (
              <div className="text-sm text-gray-600">No report has been submitted yet.</div>
            )}

            {isAssistant && !isPrimary && canEdit && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                You are the assistant assessor for this area; edits are shared with the primary assessor.
              </div>
            )}

            <div className="flex gap-2 border-b border-gray-200">
              {([
                { key: 'guide' as const, label: 'Interview Guide' },
                { key: 'scorecard' as const, label: 'Scorecard' },
                { key: 'report' as const, label: 'Report' },
              ]).map((tab) => (
                <button
                  key={tab.key}
                  type="button"
                  className={[
                    '-mb-px px-4 py-2 text-sm font-medium border-b-2',
                    activeTab === tab.key
                      ? 'border-primary-600 text-primary-700'
                      : 'border-transparent text-gray-500 hover:text-gray-700',
                  ].join(' ')}
                  onClick={() => setActiveTab(tab.key)}
                >
                  {tab.label}
                </button>
              ))}
            </div>

            {activeTab === 'guide' && (
              <InterviewGuideTab
                assessmentId={assessmentId}
                assignmentId={reportAssignmentId}
                canEdit={canEdit}
                onContinueToReport={() => setActiveTab('scorecard')}
                continueLabel="Continue to Scorecard"
              />
            )}

            {activeTab === 'scorecard' && (
              <ScorecardTab
                assessmentId={assessmentId}
                assignmentId={reportAssignmentId}
                canEdit={canEdit}
                onContinueToReport={() => setActiveTab('report')}
              />
            )}

            {activeTab === 'report' && (canEdit || latestReport) && (
              <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_22rem]">
                <div className="space-y-6">
                  {isLocked && canEdit && (
                    <div className="rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
                      This report is locked for editing. Ask an admin to return it for edits.
                    </div>
                  )}

                  {aiEnabled && (
                    <div className="flex flex-wrap items-center gap-3 rounded-lg border border-primary-200 bg-primary-50 px-4 py-3">
                      <span className="text-xs font-semibold uppercase tracking-wide text-primary-700">Writing help</span>
                      <button
                        type="button"
                        className="rounded-md bg-white px-3 py-1.5 text-xs font-medium text-primary-700 border border-primary-300 hover:bg-primary-100 disabled:opacity-50"
                        onClick={openDraftModal}
                        disabled={!hasNotes}
                        title={hasNotes ? undefined : 'Add interview notes first'}
                      >
                        Draft from my notes
                      </button>
                      <button
                        type="button"
                        className="rounded-md bg-white px-3 py-1.5 text-xs font-medium text-primary-700 border border-primary-300 hover:bg-primary-100 disabled:opacity-50"
                        onClick={openRewriteModal}
                        disabled={!content.trim()}
                        title={content.trim() ? undefined : 'Write something first'}
                      >
                        Make candidate-ready
                      </button>
                      <span className="text-xs text-gray-600">
                        Suggestions only — nothing is saved until you insert it.
                      </span>
                    </div>
                  )}

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Report title</label>
                    <input
                      type="text"
                      className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:ring-primary-500"
                      value={title}
                      onChange={(e) => { touch(); setTitle(e.target.value); }}
                      disabled={!canEditReport}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">Report notes</label>
                    <p className="text-xs text-gray-500 mb-2">These notes are shared with the candidate.</p>
                    <textarea
                      ref={contentRef}
                      className="w-full min-h-[240px] rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:ring-primary-500"
                      value={content}
                      onFocus={() => setInsertTarget('content')}
                      onChange={(e) => { touch(); setContent(e.target.value); }}
                      disabled={!canEditReport}
                    />
                  </div>

                  <div className="grid gap-4 lg:grid-cols-2">
                    <RatingSelector
                      label="Initial rating (overall)"
                      name="initialOverallRating"
                      value={initialOverallRating}
                      onChange={(value) => { touch(); setInitialOverallRating(value); }}
                      disabled={!canEditReport}
                    />
                    <RatingSelector
                      label="Initial rating (my area)"
                      name="initialAreaRating"
                      value={initialAreaRating}
                      onChange={(value) => { touch(); setInitialAreaRating(value); }}
                      disabled={!canEditReport}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">After-part notes</label>
                    <p className="text-xs text-gray-500 mb-2">
                      Internal only. These notes are not shown to candidates.
                    </p>
                    <textarea
                      ref={afterPartRef}
                      className="w-full min-h-[180px] rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 focus:border-primary-500 focus:ring-primary-500"
                      value={afterPartNotes}
                      onFocus={() => setInsertTarget('afterPartNotes')}
                      onChange={(e) => { touch(); setAfterPartNotes(e.target.value); }}
                      disabled={!canEditReport}
                    />
                  </div>
                </div>

                <aside className="lg:sticky lg:top-6 lg:self-start space-y-2">
                  <InterviewNotesPanel
                    guide={guideData}
                    onInsert={canEditReport ? insertIntoTarget : undefined}
                  />
                  <ScorecardSummary
                    scorecard={scorecardData}
                    onInsert={canEditReport ? insertIntoTarget : undefined}
                  />
                  {canEditReport && (
                    <p className="text-xs text-gray-500">
                      Insert adds to the{' '}
                      <span className="font-medium text-gray-700">
                        {insertTarget === 'afterPartNotes' ? 'after-part notes' : 'report notes'}
                      </span>
                      . Click into the other field to change where it lands.
                    </p>
                  )}
                </aside>
              </div>
            )}
          </div>
        )}
      </div>

      {activeTab === 'report' && assignment && (canEdit || latestReport) && (
        <div className="sticky bottom-0 -mx-4 border-t border-gray-200 bg-white/95 px-4 py-3 backdrop-blur sm:-mx-6 sm:px-6">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <span className={['rounded-full px-2.5 py-1 text-xs font-semibold', statusPillClass[reportStatus] || statusPillClass.draft].join(' ')}>
                {statusLabel[reportStatus] || reportStatus}
              </span>
              <span className={['text-xs', saveState === 'error' ? 'text-red-700' : 'text-gray-500'].join(' ')}>
                {saveState === 'error' ? 'Could not save — use Save Draft to retry' : saveStateLabel[saveState]}
              </span>
            </div>
            {canEditReport && (
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  className="px-4 py-2 rounded-md text-sm font-medium text-gray-700 border border-gray-300 hover:bg-gray-50 disabled:opacity-60"
                  onClick={() => handleExplicitSave('draft')}
                  disabled={saveMutation.isPending}
                >
                  Save Draft
                </button>
                <button
                  type="button"
                  className="px-4 py-2 rounded-md text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-60"
                  onClick={() => (submitIssues.length ? setShowSubmitCheck(true) : handleExplicitSave('submitted'))}
                  disabled={saveMutation.isPending}
                >
                  {isSubmitted ? 'Update Submitted Report' : 'Submit Report'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {showSubmitCheck && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="w-full max-w-md rounded-xl bg-white p-5 shadow-xl">
            <h2 className="text-lg font-semibold text-gray-900">Before you submit</h2>
            <ul className="mt-3 list-disc space-y-1 pl-5 text-sm text-gray-700">
              {submitIssues.map((issue) => (
                <li key={issue}>{issue}</li>
              ))}
            </ul>
            <p className="mt-3 text-xs text-gray-500">
              You can submit anyway and fill these in later while the report is still editable.
            </p>
            <div className="mt-5 flex justify-end gap-3">
              <button
                type="button"
                className="rounded-md border border-gray-300 px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50"
                onClick={() => setShowSubmitCheck(false)}
              >
                Go back
              </button>
              <button
                type="button"
                className="rounded-md bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700"
                onClick={() => handleExplicitSave('submitted')}
              >
                Submit anyway
              </button>
            </div>
          </div>
        </div>
      )}

      {draftModalOpen && (
        <AiDraftModal
          isLoading={draftMutation.isPending}
          error={draftError}
          result={draftResult}
          onRetry={() => {
            setDraftError(null);
            draftMutation.mutate();
          }}
          onClose={() => setDraftModalOpen(false)}
          onUseCandidateFacing={(text, mode) => {
            setContent((prev) => (mode === 'replace' ? text : appendText(prev, text)));
            noteAiUse('draftedAt', draftResult?.model);
            setDraftModalOpen(false);
          }}
          onUseInternal={(text, mode) => {
            setAfterPartNotes((prev) => (mode === 'replace' ? text : appendText(prev, text)));
            noteAiUse('draftedAt', draftResult?.model);
            setDraftModalOpen(false);
          }}
        />
      )}

      {rewriteModalOpen && (
        <AiRewriteModal
          isLoading={rewriteMutation.isPending}
          error={rewriteError}
          result={rewriteResult}
          original={rewriteSource}
          onRetry={() => {
            setRewriteError(null);
            rewriteMutation.mutate(rewriteSource);
          }}
          onClose={() => setRewriteModalOpen(false)}
          onAccept={(text) => {
            setContent(text);
            noteAiUse('rewrittenAt', rewriteResult?.model);
            setRewriteModalOpen(false);
          }}
        />
      )}
    </Layout>
  );
};

export default AssessmentReport;
